import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import api from '../services/api';

export const useVerificationStore = create(
  persist(
    (set, get) => ({
      // Current verification request context
      requestId: null,
      clientFileId: null,
      firmName: null,
      
      // Captured evidence (stored as base64 until uploaded)
      capturedEvidence: {
        idDocument: null,
        selfie: null,
        addressProof: null
      },
      
      // Upload status
      uploadStatus: {
        idDocument: null, // null, 'uploading', 'uploaded', 'failed'
        selfie: null,
        addressProof: null
      },
      
      // Evidence IDs returned from API
      evidenceIds: {
        idDocument: null,
        selfie: null,
        addressProof: null
      },

      // Set the current verification request context
      setRequest: (requestId, clientFileId, firmName) => {
        set({ requestId, clientFileId, firmName });
      },

      // Store captured evidence locally
      setCapturedEvidence: (type, data) => {
        set(state => ({
          capturedEvidence: {
            ...state.capturedEvidence,
            [type]: data
          }
        }));
      },

      // Upload evidence to API
      uploadEvidence: async (type, imageData, metadata = {}) => {
        const { clientFileId } = get();

        if (!clientFileId) {
          throw new Error('No client file ID set');
        }

        // Map API types to store keys
        const typeToStoreKey = {
          'ID_FRONT': 'idDocument',
          'ID_BACK': 'idDocument',
          'SELFIE': 'selfie',
          'POA_DOCUMENT': 'addressProof'
        };
        const storeKey = typeToStoreKey[type] || type;

        set(state => ({
          uploadStatus: { ...state.uploadStatus, [storeKey]: 'uploading' }
        }));

        try {
          // Convert base64 to blob
          const response = await fetch(imageData);
          const blob = await response.blob();

          // Create FormData for multipart upload
          const formData = new FormData();
          formData.append('file', blob, `${type}.jpg`);
          formData.append('clientFileId', clientFileId);
          formData.append('type', type);
          formData.append('metadata', JSON.stringify(metadata));

          // Upload to API
          const result = await api.post('/evidence/upload', formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });

          set(state => ({
            uploadStatus: { ...state.uploadStatus, [storeKey]: 'uploaded' },
            evidenceIds: { ...state.evidenceIds, [storeKey]: result.data.evidenceId },
            capturedEvidence: { ...state.capturedEvidence, [storeKey]: null } // Clear local data after upload
          }));

          return result.data;
        } catch (error) {
          set(state => ({
            uploadStatus: { ...state.uploadStatus, [storeKey]: 'failed' }
          }));
          throw error;
        }
      },

      // Submit the verification request
      submitVerification: async () => {
        const { requestId } = get();
        
        if (!requestId) {
          throw new Error('No request ID set');
        }
        
        const result = await api.post(`/requests/${requestId}/submit`);
        return result.data;
      },

      // Get verification progress
      getProgress: () => {
        const { uploadStatus } = get();
        const completed = Object.values(uploadStatus).filter(s => s === 'uploaded').length;
        return {
          completed,
          total: 3,
          percentage: Math.round((completed / 3) * 100)
        };
      },

      // Reset store for new verification
      reset: () => {
        set({
          requestId: null,
          clientFileId: null,
          firmName: null,
          capturedEvidence: { idDocument: null, selfie: null, addressProof: null },
          uploadStatus: { idDocument: null, selfie: null, addressProof: null },
          evidenceIds: { idDocument: null, selfie: null, addressProof: null }
        });
      }
    }),
    {
      name: 'acquiva-verification',
      partialize: (state) => ({
        requestId: state.requestId,
        clientFileId: state.clientFileId,
        firmName: state.firmName,
        uploadStatus: state.uploadStatus,
        evidenceIds: state.evidenceIds
      })
    }
  )
);

